/**
 * S3 presigned upload helpers.
 *
 *  1. POST /api/uploads/presign  -> {upload_url, fields, storage_path, ...}
 *  2. POST upload_url            -> raw XHR multipart/form-data (no auth headers!)
 *  3. GET  /api/uploads/view-url -> short-lived presigned GET for previewing
 *
 *  IMPORTANT: the S3 POST MUST be a "naked" multipart form — no Authorization
 *  header, no withCredentials, no X-Requested-With. Adding any of those triggers
 *  a CORS preflight that the bucket policy will reject, surfacing as a
 *  CORS/404 error in the browser even though Postman works fine.
 */
import axios from "axios";
import { API } from "../App";

const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Upload a single File directly to S3 via presigned POST.
 *
 * @param {File} file
 * @param {"photos"|"videos"|"documents"|"avatars"|"misc"} category
 * @param {(percent: number) => void} onProgress
 * @returns {Promise<{storage_path,filename,content_type,size}>}
 */
export async function uploadToS3(file, category, onProgress) {
  // 1) Get presigned POST from our backend
  const presignResp = await axios.post(
    `${API}/uploads/presign`,
    {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      category,
    },
    { withCredentials: true, headers: getAuthHeaders() }
  );

  const { upload_url, fields, storage_path } = presignResp.data;

  // 2) Build the multipart form EXACTLY as S3 expects:
  //    - All policy fields BEFORE the "file" field
  //    - "file" appended LAST
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  form.append("file", file);

  // 3) Raw XHR — no axios defaults, no Authorization header, no withCredentials.
  //    This is what S3's presigned POST docs recommend and matches the
  //    Postman behaviour that already works against the bucket.
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", upload_url, true);
    // DO NOT set Content-Type — the browser will set the multipart boundary.
    // DO NOT setRequestHeader("Authorization", ...).
    // DO NOT set xhr.withCredentials = true.

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      };
    }

    xhr.onload = () => {
      // S3 returns 204 No Content on success
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText} ${xhr.responseText || ""}`));
      }
    };
    xhr.onerror = () => reject(new Error("S3 upload network error (likely CORS — check bucket CORS policy)"));
    xhr.onabort = () => reject(new Error("S3 upload aborted"));

    xhr.send(form);
  });

  return {
    storage_path,
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size: file.size,
  };
}

/** Fetch a short-lived signed GET URL for a stored object. */
export async function fetchViewUrl(storage_path) {
  const resp = await axios.get(`${API}/uploads/view-url`, {
    params: { path: storage_path },
    withCredentials: true,
    headers: getAuthHeaders(),
  });
  return resp.data.url;
}
